import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SupplierActiveFilter, type InvoiceableReceiptLine } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useInvoiceableLinesQuery,
  usePostPurchaseInvoiceMutation,
  type PurchaseInvoiceLineInput,
} from '../api/purchase-invoices.api';
import { useSuppliersQuery } from '../api/suppliers.api';
import { taxProfileLabel, usePurchasingTaxProfilesQuery } from '../api/tax-profiles.api';

/** One delivered line as it is being billed. Quantity and cost stay the text that was typed until they are parsed. */
interface Entry {
  selected: boolean;
  quantityInput: string;
  costInput: string;
  taxProfileId: string;
}

const asQuantity = (value: string): number => {
  const parsed = Number(value.replace(/[^\d]/gu, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

/** What one supplier's deliveries look like once gathered by the order and the delivery they came on. */
interface Group {
  purchaseOrderId: string;
  purchaseOrderReference: string;
  receipts: { purchaseReceiptId: string; receiptReference: string; receivedOn: string; lines: InvoiceableReceiptLine[] }[];
}

function groupLines(lines: readonly InvoiceableReceiptLine[]): Group[] {
  const orders = new Map<string, Group>();

  for (const line of lines) {
    const order = orders.get(line.purchaseOrderId) ?? {
      purchaseOrderId: line.purchaseOrderId,
      purchaseOrderReference: line.purchaseOrderReference,
      receipts: [],
    };
    const receipt = order.receipts.find(
      (candidate) => candidate.purchaseReceiptId === line.purchaseReceiptId,
    ) ?? {
      purchaseReceiptId: line.purchaseReceiptId,
      receiptReference: line.receiptReference,
      receivedOn: line.receivedOn,
      lines: [],
    };

    if (!order.receipts.includes(receipt)) order.receipts.push(receipt);
    receipt.lines.push(line);
    orders.set(line.purchaseOrderId, order);
  }

  return [...orders.values()];
}

/**
 * Recording what a supplier has billed for goods already received.
 *
 * A page rather than a dialog: an invoice can cover several deliveries across several orders, each
 * line with its own quantity, its own billed cost and possibly its own tax, and that is real work.
 *
 * The supplier comes first because it decides everything else — an invoice covers one supplier, so
 * the lines on offer are only ever that supplier's. Everything is prefilled from what was delivered
 * and agreed, and every prefill is a starting point: the supplier's figures are what they are, and
 * a cost that differs from the agreed one is recorded as billed, not corrected.
 *
 * Totals shown while typing are a preview and say so. What the document is worth is whatever the
 * server works out when it is posted.
 */
export function RecordSupplierInvoicePage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { branches } = useBranch();
  const { format, parse, toInput } = useCurrency();

  const [supplierId, setSupplierId] = useState(params.get('supplierId') ?? '');
  const [branchId, setBranchId] = useState('');
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [entries, setEntries] = useState<Record<string, Entry>>({});

  const suppliers = useSuppliersQuery({ pageSize: 100, active: SupplierActiveFilter.ALL });
  const taxProfiles = usePurchasingTaxProfilesQuery();
  const open = useInvoiceableLinesQuery(
    { supplierId, branchId, pageSize: 100 },
    { enabled: Boolean(supplierId) },
  );
  const post = usePostPurchaseInvoiceMutation();

  const lines = useMemo(() => open.data?.items ?? [], [open.data]);

  // Each delivered line arrives with what it is owed for already filled in; nothing is selected until someone says so.
  useEffect(() => {
    setEntries((current) => {
      const next: Record<string, Entry> = {};
      for (const line of lines) {
        next[line.purchaseReceiptLineId] = current[line.purchaseReceiptLineId] ?? {
          selected: false,
          quantityInput: String(line.quantityInvoiceable),
          costInput: toInput(line.agreedUnitCostMinor),
          taxProfileId: line.taxProfileId ?? '',
        };
      }
      return next;
    });
  }, [lines, toInput]);

  const entryFor = (line: InvoiceableReceiptLine): Entry =>
    entries[line.purchaseReceiptLineId] ?? {
      selected: false,
      quantityInput: String(line.quantityInvoiceable),
      costInput: toInput(line.agreedUnitCostMinor),
      taxProfileId: line.taxProfileId ?? '',
    };

  const setEntry = (line: InvoiceableReceiptLine, patch: Partial<Entry>) =>
    setEntries((current) => ({
      ...current,
      [line.purchaseReceiptLineId]: { ...entryFor(line), ...patch },
    }));

  const chosen = lines.filter((line) => entryFor(line).selected);
  const branchesChosen = new Set(chosen.map((line) => line.branchId));

  const rateOf = (taxProfileId: string, fallback: number): number =>
    taxProfiles.data?.find((profile) => profile.id === taxProfileId)?.rateBasisPoints ?? fallback;

  const preview = chosen.reduce((total, line) => {
    const entry = entryFor(line);
    const net = asQuantity(entry.quantityInput) * (parse(entry.costInput) ?? 0);
    // A purchase cost is billed before tax, so the tax is added on top — never extracted.
    return total + net + Math.round((net * rateOf(entry.taxProfileId, line.taxRateBasisPoints)) / 10_000);
  }, 0);

  const overBilled = chosen.filter(
    (line) => asQuantity(entryFor(line).quantityInput) > line.quantityInvoiceable,
  );
  const emptyQuantity = chosen.filter((line) => asQuantity(entryFor(line).quantityInput) < 1);

  const payload = (): PurchaseInvoiceLineInput[] =>
    chosen.map((line) => {
      const entry = entryFor(line);
      return {
        purchaseReceiptLineId: line.purchaseReceiptLineId,
        quantityInvoiced: asQuantity(entry.quantityInput),
        billedUnitCostMinor: parse(entry.costInput) ?? 0,
        ...(entry.taxProfileId ? { taxProfileId: entry.taxProfileId } : {}),
      };
    });

  const canPost =
    Boolean(supplierId) &&
    supplierInvoiceNumber.trim().length > 0 &&
    Boolean(invoiceDate) &&
    chosen.length > 0 &&
    overBilled.length === 0 &&
    emptyQuantity.length === 0 &&
    branchesChosen.size <= 1 &&
    !post.isPending;

  const submit = () => {
    if (!canPost) return;
    post.mutate(
      {
        supplierInvoiceNumber: supplierInvoiceNumber.trim(),
        invoiceDate,
        lines: payload(),
      },
      { onSuccess: (invoice) => void navigate(`/purchasing/invoices/${invoice.id}`) },
    );
  };

  const groups = groupLines(lines);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('purchasing.invoices.recordTitle')}
        subtitle={t('purchasing.invoices.recordSubtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => void navigate('/purchasing/invoices')}
              disabled={post.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} isLoading={post.isPending} disabled={!canPost}>
              {t('purchasing.invoices.post')}
            </Button>
          </div>
        }
      />

      {post.isError ? (
        <>
          <Alert tone="error">
            {apiErrorMessage(post.error) ?? t('purchasing.invoices.postError')}
          </Alert>
          <Alert tone="info">{t('purchasing.invoices.inputKept')}</Alert>
        </>
      ) : null}
      {branchesChosen.size > 1 ? (
        <Alert tone="warning">{t('purchasing.invoices.oneBranchOnly')}</Alert>
      ) : null}
      {overBilled.length > 0 ? (
        <Alert tone="warning">{t('purchasing.invoices.overInvoiceWarning')}</Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('purchasing.invoices.documentSection')}
          subtitle={t('purchasing.invoices.documentHint')}
        />
        <CardBody className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FormField label={t('purchasing.orders.fields.supplier')} required>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                options={[
                  { value: '', label: t('purchasing.invoices.chooseSupplier') },
                  ...(suppliers.data?.items ?? []).map((supplier) => ({
                    value: supplier.id,
                    label: supplier.name,
                  })),
                ]}
                value={supplierId}
                onChange={(event) => {
                  setSupplierId(event.target.value);
                  setEntries({});
                }}
              />
            )}
          </FormField>

          <FormField
            label={t('purchasing.orders.fields.branch')}
            hint={t('purchasing.invoices.branchHint')}
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                options={[
                  { value: '', label: t('purchasing.orders.filters.allBranches') },
                  ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
                ]}
                value={branchId}
                onChange={(event) => {
                  setBranchId(event.target.value);
                  setEntries({});
                }}
              />
            )}
          </FormField>

          <Input
            label={t('purchasing.invoices.fields.supplierInvoiceNumber')}
            required
            dir="ltr"
            value={supplierInvoiceNumber}
            hint={t('purchasing.invoices.fields.supplierInvoiceNumberHint')}
            onChange={(event) => setSupplierInvoiceNumber(event.target.value)}
          />

          <Input
            type="date"
            label={t('purchasing.invoices.fields.invoiceDate')}
            required
            value={invoiceDate}
            hint={t('purchasing.invoices.fields.invoiceDateHint')}
            onChange={(event) => setInvoiceDate(event.target.value)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('purchasing.invoices.linesSection')}
          subtitle={t('purchasing.invoices.linesHint')}
        />
        <CardBody className="space-y-4">
          {!supplierId ? (
            <p className="text-muted text-sm">{t('purchasing.invoices.chooseSupplierFirst')}</p>
          ) : open.isPending ? (
            <p className="text-muted text-sm">{t('common.loading')}</p>
          ) : open.isError ? (
            <Alert tone="error">
              {apiErrorMessage(open.error) ?? t('purchasing.invoices.invoiceableLoadError')}
            </Alert>
          ) : lines.length === 0 ? (
            <p className="text-muted text-sm">{t('purchasing.invoices.nothingToBill')}</p>
          ) : (
            groups.map((group) => (
              <section
                key={group.purchaseOrderId}
                className="border-line space-y-3 rounded-lg border p-3"
                aria-label={group.purchaseOrderReference}
              >
                <p className="text-ink text-sm font-medium" dir="ltr">
                  {group.purchaseOrderReference}
                </p>

                {group.receipts.map((receipt) => (
                  <div key={receipt.purchaseReceiptId} className="space-y-3">
                    <div className="text-muted flex flex-wrap items-center gap-3 text-xs">
                      <span dir="ltr">{receipt.receiptReference}</span>
                      <span dir="ltr">{receipt.receivedOn}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setEntries((current) => {
                            const next = { ...current };
                            for (const line of receipt.lines) {
                              next[line.purchaseReceiptLineId] = {
                                ...entryFor(line),
                                selected: true,
                                quantityInput: String(line.quantityInvoiceable),
                              };
                            }
                            return next;
                          })
                        }
                      >
                        {t('purchasing.invoices.selectWholeReceipt')}
                      </Button>
                    </div>

                    {receipt.lines.map((line) => {
                      const entry = entryFor(line);
                      const billed = parse(entry.costInput) ?? 0;
                      const variance = billed - line.agreedUnitCostMinor;
                      const tooMany =
                        asQuantity(entry.quantityInput) > line.quantityInvoiceable;

                      return (
                        <div
                          key={line.purchaseReceiptLineId}
                          className="bg-canvas grid gap-3 rounded-lg p-3 lg:grid-cols-[auto_1fr_7rem_9rem_12rem] lg:items-end"
                        >
                          <input
                            type="checkbox"
                            className="size-4 self-center lg:mb-2.5"
                            checked={entry.selected}
                            aria-label={t('purchasing.invoices.selectLine', { sku: line.sku })}
                            onChange={(event) =>
                              setEntry(line, { selected: event.target.checked })
                            }
                          />

                          <div className="min-w-0">
                            <p className="text-ink text-sm" dir="auto">
                              {line.variantName ?? line.productName}
                            </p>
                            <p className="text-muted font-mono text-xs" dir="ltr">
                              {line.sku}
                              {line.lotNumber ? ` · ${line.lotNumber}` : ''}
                            </p>
                            <p className="text-muted text-xs">
                              {t('purchasing.invoices.receivedAndBilled', {
                                received: line.quantityReceived,
                                invoiced: line.quantityInvoiced,
                                remaining: line.quantityInvoiceable,
                              })}
                            </p>
                          </div>

                          <Input
                            label={t('purchasing.invoices.fields.quantityToInvoice')}
                            dir="ltr"
                            inputMode="numeric"
                            value={entry.quantityInput}
                            error={tooMany ? t('purchasing.invoices.tooMany') : undefined}
                            aria-label={t('purchasing.invoices.quantityFor', { sku: line.sku })}
                            onChange={(event) =>
                              setEntry(line, {
                                quantityInput: event.target.value.replace(/[^\d]/gu, ''),
                              })
                            }
                          />

                          <div className="space-y-1">
                            <Input
                              label={t('purchasing.invoices.fields.billedUnitCost')}
                              dir="ltr"
                              inputMode="decimal"
                              value={entry.costInput}
                              aria-label={t('purchasing.invoices.costFor', { sku: line.sku })}
                              onChange={(event) => setEntry(line, { costInput: event.target.value })}
                            />
                            <p className="text-muted text-xs" dir="auto">
                              {variance === 0
                                ? t('purchasing.invoices.matchesAgreed', {
                                    agreed: format(line.agreedUnitCostMinor),
                                  })
                                : t('purchasing.invoices.variance', {
                                    agreed: format(line.agreedUnitCostMinor),
                                    variance: format(Math.abs(variance)),
                                    context: variance > 0 ? 'above' : 'below',
                                  })}
                            </p>
                          </div>

                          <Select
                            options={(taxProfiles.data ?? []).map((profile) => ({
                              value: profile.id,
                              label: taxProfileLabel(profile, locale, (treatment) =>
                                t(`sales.taxTreatments.${treatment}`),
                              ),
                            }))}
                            value={entry.taxProfileId}
                            aria-label={t('purchasing.invoices.taxFor', { sku: line.sku })}
                            onChange={(event) =>
                              setEntry(line, { taxProfileId: event.target.value })
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </section>
            ))
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('purchasing.invoices.reviewSection')} />
        <CardBody className="space-y-2 text-sm">
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('purchasing.invoices.reviewLines')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {chosen.length}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('purchasing.invoices.reviewQuantity')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {chosen.reduce(
                (total, line) => total + asQuantity(entryFor(line).quantityInput),
                0,
              )}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('purchasing.invoices.previewTotal')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {format(preview)}
            </span>
          </div>
          <p className="text-muted text-xs">{t('purchasing.invoices.previewHint')}</p>
        </CardBody>
      </Card>
    </div>
  );
}
